import React, { useState, useEffect } from 'react';
import { Storage } from 'aws-amplify';
import './Settings.css';

const Settings = ({ user, company }) => {
  const [activeTab, setActiveTab] = useState('account');
  const [settings, setSettings] = useState({
    // Account settings
    fullName: '',
    email: '',
    phone: '',

    // Notification settings
    emailNotifications: true,
    uploadNotifications: true,
    weeklyDigest: true,

    // Security settings
    twoFactorEnabled: false,
    sessionTimeout: 30,

    // Company branding (admin only)
    companyName: '',
    primaryColor: '#3b82f6',
    secondaryColor: '#1e40af',
    logoUrl: '',

    // File settings
    defaultRetention: 365,
    storageQuota: 100,
    autoSync: true,

    // System preferences
    timezone: 'America/New_York',
    dateFormat: 'MM/DD/YYYY',
    language: 'en'
  });

  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [changes, setChanges] = useState({});

  useEffect(() => {
    loadSettings();
  }, [user, company]);

  const loadSettings = () => {
    // Load user settings from user attributes and company data
    if (user) {
      setSettings(prev => ({
        ...prev,
        fullName: user.attributes?.name || '',
        email: user.attributes?.email || user.username || '',
        phone: user.attributes?.phone_number || '',
        companyName: company?.name || '',
        primaryColor: company?.branding?.primaryColor || '#3b82f6',
        secondaryColor: company?.branding?.secondaryColor || '#1e40af',
        logoUrl: company?.branding?.logoUrl || ''
      }));
    }
  };

  const handleInputChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
    setChanges(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadingLogo(true);
    try {
      // In a real implementation, this would upload to S3
      const logoKey = `company-logos/${company.id}/${file.name}`;

      // Simulate upload for now
      await new Promise(resolve => setTimeout(resolve, 2000));

      const logoUrl = URL.createObjectURL(file);
      handleInputChange('logoUrl', logoUrl);

      console.log(`Logo would be uploaded to S3 key: ${logoKey}`);
    } catch (error) {
      console.error('Error uploading logo:', error);
      alert('Error uploading logo. Please try again.');
    } finally {
      setUploadingLogo(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      // In a real implementation, this would save to backend
      await new Promise(resolve => setTimeout(resolve, 1000));

      console.log('Settings would be saved:', changes);
      setChanges({});
      alert('Settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Error saving settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'account', label: 'Account', icon: '👤' },
    { id: 'notifications', label: 'Notifications', icon: '🔔' },
    { id: 'security', label: 'Security', icon: '🔒' },
    ...(company?.type === 'admin' ? [
      { id: 'branding', label: 'Branding', icon: '🎨' },
      { id: 'users', label: 'User Management', icon: '👥' }
    ] : []),
    { id: 'files', label: 'Files & Storage', icon: '📁' },
    { id: 'integrations', label: 'Integrations', icon: '🔗' },
    { id: 'preferences', label: 'Preferences', icon: '⚙️' }
  ];

  const hasChanges = Object.keys(changes).length > 0;

  return (
    <div className="settings">
      <div className="settings-header">
        <h1>Settings</h1>
        <p>Manage your account and preferences</p>
      </div>

      <div className="settings-content">
        {/* Sidebar Navigation */}
        <div className="settings-sidebar">
          <nav className="settings-nav">
            {tabs.map(tab => (
              <button
                key={tab.id}
                className={`settings-tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <span className="tab-icon">{tab.icon}</span>
                <span className="tab-label">{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Main Content */}
        <div className="settings-main">
          {/* Account Settings */}
          {activeTab === 'account' && (
            <div className="settings-section">
              <h2>Account Information</h2>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="fullName">Full Name</label>
                  <input
                    id="fullName"
                    type="text"
                    value={settings.fullName}
                    onChange={(e) => handleInputChange('fullName', e.target.value)}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <input
                    id="email"
                    type="email"
                    value={settings.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="phone">Phone Number</label>
                  <input
                    id="phone"
                    type="tel"
                    value={settings.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    placeholder="+1 (555) 123-4567"
                    className="form-input"
                  />
                </div>
              </div>

              <div className="form-section">
                <h3>Change Password</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="currentPassword">Current Password</label>
                    <input
                      id="currentPassword"
                      type="password"
                      className="form-input"
                      placeholder="Enter current password"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="newPassword">New Password</label>
                    <input
                      id="newPassword"
                      type="password"
                      className="form-input"
                      placeholder="Enter new password"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="confirmPassword">Confirm New Password</label>
                    <input
                      id="confirmPassword"
                      type="password"
                      className="form-input"
                      placeholder="Confirm new password"
                    />
                  </div>
                </div>
                <button className="btn-secondary">Update Password</button>
              </div>
            </div>
          )}

          {/* Notification Settings */}
          {activeTab === 'notifications' && (
            <div className="settings-section">
              <h2>Notification Preferences</h2>
              <div className="form-section">
                <h3>Email Notifications</h3>
                <div className="checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={settings.emailNotifications}
                      onChange={(e) => handleInputChange('emailNotifications', e.target.checked)}
                    />
                    <span className="checkbox-text">Email notifications enabled</span>
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={settings.uploadNotifications}
                      onChange={(e) => handleInputChange('uploadNotifications', e.target.checked)}
                    />
                    <span className="checkbox-text">File upload notifications</span>
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={settings.weeklyDigest}
                      onChange={(e) => handleInputChange('weeklyDigest', e.target.checked)}
                    />
                    <span className="checkbox-text">Weekly activity digest</span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Security Settings */}
          {activeTab === 'security' && (
            <div className="settings-section">
              <h2>Security Settings</h2>
              <div className="form-section">
                <h3>Authentication</h3>
                <div className="checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={settings.twoFactorEnabled}
                      onChange={(e) => handleInputChange('twoFactorEnabled', e.target.checked)}
                    />
                    <span className="checkbox-text">Enable two-factor authentication</span>
                  </label>
                </div>

                <div className="form-group">
                  <label htmlFor="sessionTimeout">Session Timeout (minutes)</label>
                  <select
                    id="sessionTimeout"
                    value={settings.sessionTimeout}
                    onChange={(e) => handleInputChange('sessionTimeout', parseInt(e.target.value))}
                    className="form-select"
                  >
                    <option value={15}>15 minutes</option>
                    <option value={30}>30 minutes</option>
                    <option value={60}>1 hour</option>
                    <option value={120}>2 hours</option>
                    <option value={480}>8 hours</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Company Branding (Admin only) */}
          {activeTab === 'branding' && company?.type === 'admin' && (
            <div className="settings-section">
              <h2>Company Branding</h2>
              <div className="form-section">
                <div className="form-group">
                  <label htmlFor="companyName">Company Name</label>
                  <input
                    id="companyName"
                    type="text"
                    value={settings.companyName}
                    onChange={(e) => handleInputChange('companyName', e.target.value)}
                    className="form-input"
                  />
                </div>

                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="primaryColor">Primary Color</label>
                    <div className="color-input-group">
                      <input
                        id="primaryColor"
                        type="color"
                        value={settings.primaryColor}
                        onChange={(e) => handleInputChange('primaryColor', e.target.value)}
                        className="color-input"
                      />
                      <input
                        type="text"
                        value={settings.primaryColor}
                        onChange={(e) => handleInputChange('primaryColor', e.target.value)}
                        className="form-input"
                        placeholder="#3b82f6"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label htmlFor="secondaryColor">Secondary Color</label>
                    <div className="color-input-group">
                      <input
                        id="secondaryColor"
                        type="color"
                        value={settings.secondaryColor}
                        onChange={(e) => handleInputChange('secondaryColor', e.target.value)}
                        className="color-input"
                      />
                      <input
                        type="text"
                        value={settings.secondaryColor}
                        onChange={(e) => handleInputChange('secondaryColor', e.target.value)}
                        className="form-input"
                        placeholder="#1e40af"
                      />
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="logo">Company Logo</label>
                  <div className="logo-upload">
                    {settings.logoUrl && (
                      <div className="logo-preview">
                        <img src={settings.logoUrl} alt="Company logo" />
                      </div>
                    )}
                    <input
                      id="logo"
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="file-input"
                      disabled={uploadingLogo}
                    />
                    <label htmlFor="logo" className="file-upload-btn">
                      {uploadingLogo ? 'Uploading...' : 'Choose Logo'}
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Files & Storage */}
          {activeTab === 'files' && (
            <div className="settings-section">
              <h2>Files & Storage</h2>
              <div className="form-section">
                <div className="form-group">
                  <label htmlFor="defaultRetention">Default File Retention (days)</label>
                  <input
                    id="defaultRetention"
                    type="number"
                    value={settings.defaultRetention}
                    onChange={(e) => handleInputChange('defaultRetention', parseInt(e.target.value))}
                    className="form-input"
                    min="30"
                    max="3650"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="storageQuota">Storage Quota per Client (GB)</label>
                  <input
                    id="storageQuota"
                    type="number"
                    value={settings.storageQuota}
                    onChange={(e) => handleInputChange('storageQuota', parseInt(e.target.value))}
                    className="form-input"
                    min="1"
                    max="1000"
                  />
                </div>

                <div className="checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={settings.autoSync}
                      onChange={(e) => handleInputChange('autoSync', e.target.checked)}
                    />
                    <span className="checkbox-text">Enable automatic folder synchronization</span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Preferences */}
          {activeTab === 'preferences' && (
            <div className="settings-section">
              <h2>System Preferences</h2>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="timezone">Time Zone</label>
                  <select
                    id="timezone"
                    value={settings.timezone}
                    onChange={(e) => handleInputChange('timezone', e.target.value)}
                    className="form-select"
                  >
                    <option value="America/New_York">Eastern Time</option>
                    <option value="America/Chicago">Central Time</option>
                    <option value="America/Denver">Mountain Time</option>
                    <option value="America/Los_Angeles">Pacific Time</option>
                    <option value="UTC">UTC</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="dateFormat">Date Format</label>
                  <select
                    id="dateFormat"
                    value={settings.dateFormat}
                    onChange={(e) => handleInputChange('dateFormat', e.target.value)}
                    className="form-select"
                  >
                    <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                    <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                    <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="language">Language</label>
                  <select
                    id="language"
                    value={settings.language}
                    onChange={(e) => handleInputChange('language', e.target.value)}
                    className="form-select"
                  >
                    <option value="en">English</option>
                    <option value="es">Spanish</option>
                    <option value="fr">French</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Other tabs placeholder */}
          {(activeTab === 'users' || activeTab === 'integrations') && (
            <div className="settings-section">
              <h2>{tabs.find(t => t.id === activeTab)?.label}</h2>
              <div className="placeholder-content">
                <div className="placeholder-icon">🚧</div>
                <h3>Coming Soon</h3>
                <p>This section is under development and will be available in a future update.</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Save Button */}
      {hasChanges && (
        <div className="settings-footer">
          <div className="save-indicator">
            <span>You have unsaved changes</span>
            <button
              onClick={saveSettings}
              disabled={saving}
              className="btn-primary"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;